import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, MapPin, Heart, ClipboardList, Bell, HelpCircle, LogOut, ChevronRight, Shield, FileText, Gift, Mail, ShieldCheck, ShieldOff, Check, AlertCircle } from 'lucide-react'
import TopHeader from '../components/layout/TopHeader.jsx'
import { useAuthStore, useWishlistStore, useCartStore } from '../store/index.js'
import { authService, addressService, promotionService } from '../services/api.js'
import toast from 'react-hot-toast'

function MenuItem({ icon: Icon, label, value, onClick, danger, badge }) {
  return (
    <button onClick={onClick} className="flex items-center gap-3 w-full px-4 py-3.5 active:bg-surface-border/30 transition-colors">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${danger ? 'bg-red-900/20' : 'bg-surface-elevated'}`}>
        <Icon size={18} className={danger ? 'text-red-400' : 'text-gray-300'} />
      </div>
      <span className={`flex-1 font-display font-medium text-left ${danger ? 'text-red-400' : 'text-gray-200'}`}>{label}</span>
      {value && <span className="text-gray-500 text-sm font-display">{value}</span>}
      {badge && <span className="badge bg-primary text-white text-[10px]">{badge}</span>}
      {!danger && <ChevronRight size={16} className="text-gray-600" />}
    </button>
  )
}

function AddressesSection({ onBack }) {
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading]     = useState(true)

  useEffect(() => {
    addressService.getAll()
      .then(data => setAddresses(Array.isArray(data) ? data : (data?.addresses || [])))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="page-container">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-surface-border flex items-center gap-3 px-4 h-14" style={{ paddingTop: 'var(--safe-top)' }}>
        <button onClick={onBack} className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <span className="font-heading text-white text-lg">Delivery Address</span>
      </div>

      <div className="px-4 pt-4 pb-8 space-y-4">
        {/* Policy notice */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-start gap-3">
          <MapPin size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-display font-semibold text-white text-sm">Delivery address policy</p>
            <p className="font-display text-gray-400 text-sm mt-1 leading-relaxed">
              As a licensed bulk distributor, deliveries can only be made to your registered address on file.
              To update your delivery address, please contact us on{' '}
              <a href="tel:0123231520" className="text-primary font-medium">012 323 1520</a>.
            </p>
          </div>
        </div>

        {/* Address cards */}
        {loading ? (
          <div className="flex justify-center pt-6">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : addresses.length === 0 ? (
          <div className="text-center pt-8">
            <MapPin size={40} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 font-display text-sm mb-1">No address on file</p>
            <p className="text-gray-500 font-display text-xs">Contact us to register your delivery address</p>
            <a href="tel:0123231520" className="mt-4 inline-flex items-center gap-2 btn-primary py-2.5 px-5 text-sm">
              📞 Call Us
            </a>
          </div>
        ) : (
          addresses.map((addr, idx) => (
            <div key={addr.id} className="bg-surface-card rounded-2xl border border-surface-border p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <MapPin size={15} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display font-semibold text-white text-sm">
                      {addr.address_type || addr.label || 'Delivery Address'}
                    </span>
                    {idx === 0 && (
                      <span className="badge bg-primary/20 text-primary text-[10px]">Default</span>
                    )}
                  </div>
                  <p className="font-display text-gray-300 text-sm">{addr.address_line1 || addr.street}</p>
                  {(addr.address_line2 || addr.suburb) && (
                    <p className="font-display text-gray-400 text-sm">{addr.address_line2 || addr.suburb}</p>
                  )}
                  <p className="font-display text-gray-400 text-sm">
                    {[addr.city, addr.postal_code].filter(Boolean).join(', ')}
                  </p>
                  {addr.province && <p className="font-display text-gray-500 text-sm">{addr.province}</p>}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Contact to update */}
        {!loading && addresses.length > 0 && (
          <div className="bg-surface-card rounded-2xl border border-surface-border p-4 flex items-center justify-between">
            <div>
              <p className="font-display font-semibold text-white text-sm">Need to update?</p>
              <p className="font-display text-gray-400 text-xs mt-0.5">Call us to change your address</p>
            </div>
            <a href="tel:0123231520" className="btn-primary py-2 px-4 text-sm whitespace-nowrap">
              📞 Call Us
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

// ── 2FA Section ───────────────────────────────────────────────────────────────
function TwoFactorSection({ onBack }) {
  const [status, setStatus]   = useState(null)   // null=loading | { totp_enabled, email_2fa_enabled }
  const [step, setStep]       = useState('idle')  // idle | choose_method | setup_totp | confirm_disable
  const [qrUrl, setQrUrl]     = useState(null)
  const [secret, setSecret]   = useState(null)
  const [code, setCode]       = useState('')
  const [loading, setLoading] = useState(false)

  const loadStatus = () => {
    authService.get2faStatus()
      .then(r => setStatus({ totp_enabled: r?.totp_enabled ?? r?.enabled ?? false, email_2fa_enabled: r?.email_2fa_enabled ?? false }))
      .catch(() => setStatus({ totp_enabled: false, email_2fa_enabled: false }))
  }
  useEffect(() => { loadStatus() }, [])

  const isEnabled    = status?.totp_enabled || status?.email_2fa_enabled
  const activeMethod = status?.totp_enabled ? 'Authenticator App' : status?.email_2fa_enabled ? 'Email OTP' : null

  const handleSetupTotp = async () => {
    setLoading(true)
    try {
      const r = await authService.setup2fa()
      setQrUrl(r.qr_url); setSecret(r.secret); setCode(''); setStep('setup_totp')
    } catch (err) { toast.error(err.message || 'Failed to start 2FA setup') }
    finally { setLoading(false) }
  }

  const handleVerifyTotp = async () => {
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return }
    setLoading(true)
    try {
      await authService.verify2fa(code)
      toast.success('Authenticator 2FA enabled!')
      loadStatus(); setStep('idle'); setQrUrl(null); setSecret(null); setCode('')
    } catch (err) { toast.error(err.message || 'Invalid code — try again') }
    finally { setLoading(false) }
  }

  const handleEnableEmail = async () => {
    setLoading(true)
    try {
      await authService.enableEmailOtp()
      toast.success('Email 2FA enabled! A code will be sent to your email each time you sign in.')
      loadStatus(); setStep('idle')
    } catch (err) { toast.error(err.message || 'Failed to enable email 2FA') }
    finally { setLoading(false) }
  }

  const handleDisable = async () => {
    setLoading(true)
    try {
      if (status?.totp_enabled)      await authService.disable2fa()
      if (status?.email_2fa_enabled) await authService.disableEmailOtp()
      toast.success('2FA disabled')
      loadStatus(); setStep('idle')
    } catch (err) { toast.error('Failed to disable 2FA') }
    finally { setLoading(false) }
  }

  const Spinner = ({ color = 'white' }) => (
    <div className={`w-4 h-4 border-2 border-${color}/30 border-t-${color} rounded-full animate-spin`} />
  )

  return (
    <div className="page-container">
      <div className="fixed top-0 left-0 right-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-surface-border flex items-center gap-3 px-4 h-14" style={{ paddingTop: 'var(--safe-top)' }}>
        <button onClick={onBack} className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <span className="font-heading text-white text-lg">Two-Factor Auth</span>
        {status !== null && isEnabled  && <span className="ml-auto text-xs bg-green-900/30 text-green-400 px-2.5 py-1 rounded-full font-display font-semibold">{activeMethod}</span>}
        {status !== null && !isEnabled && <span className="ml-auto text-xs bg-surface-elevated text-gray-500 px-2.5 py-1 rounded-full font-display">Not configured</span>}
      </div>

      <div className="px-4 pt-4 pb-8 space-y-4">
        {status === null ? (
          <div className="flex justify-center pt-8"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="bg-surface-card rounded-2xl border border-surface-border p-4">

            {/* ── Idle ── */}
            {step === 'idle' && (
              <>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Shield size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-white text-sm">Two-Factor Authentication</p>
                    <p className="font-display text-gray-400 text-sm mt-1 leading-relaxed">
                      {isEnabled
                        ? `Your account is protected with ${activeMethod}. Each login requires a verification code.`
                        : "Add an extra layer of security. Choose between an authenticator app or a code sent to your email."}
                    </p>
                  </div>
                </div>
                {isEnabled ? (
                  <button onClick={() => setStep('confirm_disable')}
                    className="w-full py-3 rounded-xl border border-red-900/50 text-red-400 font-display font-semibold text-sm active:bg-red-900/20 transition-colors">
                    Disable 2FA
                  </button>
                ) : (
                  <button onClick={() => setStep('choose_method')} disabled={loading}
                    className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-sm">
                    {loading ? <Spinner /> : <><Shield size={14} /> Enable 2FA</>}
                  </button>
                )}
              </>
            )}

            {/* ── Choose method ── */}
            {step === 'choose_method' && (
              <div className="space-y-3">
                <p className="font-display text-gray-400 text-sm mb-3 leading-relaxed">
                  Choose how you'd like to receive your verification code each time you sign in:
                </p>

                <button onClick={handleSetupTotp} disabled={loading}
                  className="w-full flex items-start gap-3 p-3 rounded-xl border border-surface-border active:bg-primary/10 transition-colors text-left">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-white text-sm">Authenticator App</p>
                    <p className="font-display text-gray-400 text-xs mt-0.5 leading-relaxed">
                      Use Google Authenticator, Authy, or any TOTP app. Works offline.
                    </p>
                  </div>
                </button>

                <button onClick={handleEnableEmail} disabled={loading}
                  className="w-full flex items-start gap-3 p-3 rounded-xl border border-surface-border active:bg-primary/10 transition-colors text-left">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <Mail size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-white text-sm">Email Code</p>
                    <p className="font-display text-gray-400 text-xs mt-0.5 leading-relaxed">
                      A 6-digit code will be sent to your registered email. No app required.
                    </p>
                  </div>
                </button>

                <button onClick={() => setStep('idle')}
                  className="w-full text-center text-sm text-gray-500 font-display py-2">
                  Cancel
                </button>
              </div>
            )}

            {/* ── Setup TOTP: QR + code ── */}
            {step === 'setup_totp' && (
              <div className="space-y-4">
                <p className="text-gray-400 text-sm font-display leading-relaxed">
                  Scan this QR code with <span className="text-white font-semibold">Google Authenticator</span> or any TOTP app, then enter the 6-digit code.
                </p>
                {qrUrl && (
                  <div className="flex justify-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUrl)}&size=200x200&bgcolor=ffffff`}
                      alt="2FA QR Code"
                      className="w-48 h-48 rounded-xl border border-surface-border"
                    />
                  </div>
                )}
                {secret && (
                  <div className="bg-surface-elevated rounded-xl px-3 py-2 text-center">
                    <p className="text-gray-500 text-xs font-display mb-1">Manual entry key</p>
                    <code className="text-xs font-mono text-primary tracking-widest">{secret}</code>
                  </div>
                )}
                <input
                  type="text" inputMode="numeric" maxLength={6} value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="input-field text-center text-2xl tracking-[0.5em] font-mono py-4"
                  autoComplete="one-time-code" autoFocus
                />
                <div className="flex gap-3">
                  <button onClick={() => { setStep('choose_method'); setCode('') }}
                    className="btn-outline flex-1 py-3 text-sm">Back</button>
                  <button onClick={handleVerifyTotp} disabled={loading || code.length < 6}
                    className="btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                    {loading ? <Spinner /> : 'Verify & Enable'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Confirm disable ── */}
            {step === 'confirm_disable' && (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm font-display">
                  Are you sure you want to disable two-factor authentication? Your account will be less secure.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setStep('idle')} className="btn-outline flex-1 py-3 text-sm">Cancel</button>
                  <button onClick={handleDisable} disabled={loading}
                    className="flex-1 py-3 rounded-xl bg-red-900/30 border border-red-900/50 text-red-400 font-display font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                    {loading ? <Spinner color="red-400" /> : 'Disable'}
                  </button>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  )
}

export default function Account() {
  const navigate = useNavigate()
  const { isAuthenticated, user, logout } = useAuthStore()
  const wishlistCount = useWishlistStore(s => s.items.length)
  const cartCount = useCartStore(s => s.count)
  const [view, setView] = useState('main') // 'main' | 'addresses' | '2fa'
  const [profile, setProfile] = useState(user || {})

  const [unreadPromos, setUnreadPromos] = useState(0)

  useEffect(() => {
    if (isAuthenticated) {
      authService.customerMe().then(data => {
        setProfile(data)
      }).catch(() => {})
      // Fetch promo unread count
      promotionService.getAll().then(data => {
        const list = Array.isArray(data) ? data : []
        setUnreadPromos(list.filter(p => !p.read && !p.dismissed).length)
      }).catch(() => {})
    }
  }, [isAuthenticated])

  const handleLogout = () => {
    logout()
    toast.success('Logged out')
  }

  if (view === 'addresses') return <AddressesSection onBack={() => setView('main')} />
  if (view === '2fa')       return <TwoFactorSection onBack={() => setView('main')} />

  if (!isAuthenticated) return (
    <div className="page-container">
      <TopHeader title="Account" />
      <div className="flex flex-col items-center px-8 pt-10 space-y-4">
        <img
          src="/logo.text-w.png"
          alt="Tony's Liquor"
          className="h-12 w-auto object-contain mb-2"
        />
        <div className="text-center">
          <h2 className="font-heading text-white text-xl">Welcome!</h2>
          <p className="font-display text-gray-400 text-sm mt-1">Sign in to access your account</p>
        </div>
        <button onClick={() => navigate('/login')} className="btn-primary w-full py-4 text-base">Sign In</button>
      </div>
    </div>
  )

  return (
    <div className="page-container">
      <TopHeader title="Account" />
      <div className="space-y-4 pt-2 pb-4">
        {/* Profile header */}
        <div className="mx-4 bg-gradient-to-br from-primary-dark to-primary rounded-2xl p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
            <span className="font-heading text-white text-2xl">
              {(profile.first_name || profile.name || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-white text-xl">
              {profile.first_name ? `${profile.first_name} ${profile.last_name || ''}` : profile.name || 'Customer'}
            </h2>
            <p className="font-display text-gray-300 text-sm">{profile.email}</p>
            {profile.loyalty_points > 0 && (
              <p className="font-display text-gold text-xs mt-0.5">⭐ {profile.loyalty_points} loyalty points</p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="mx-4 grid grid-cols-3 gap-3">
          {[
            { label: 'Orders', value: profile.total_orders || '0', icon: '📦' },
            { label: 'Wishlist', value: wishlistCount, icon: '❤️' },
            { label: 'Cart', value: cartCount, icon: '🛒' },
          ].map(stat => (
            <div key={stat.label} className="bg-surface-card rounded-2xl border border-surface-border p-3 text-center">
              <div className="text-xl mb-1">{stat.icon}</div>
              <div className="font-heading text-white text-lg">{stat.value}</div>
              <div className="text-gray-500 text-xs font-display">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Menu sections */}
        <div className="bg-surface-card rounded-2xl border border-surface-border mx-4 overflow-hidden">
          <MenuItem icon={ClipboardList} label="My Orders" onClick={() => navigate('/orders')} />
          <div className="h-px bg-surface-border mx-4" />
          <MenuItem icon={FileText} label="Order Templates" onClick={() => navigate('/templates')} />
          <div className="h-px bg-surface-border mx-4" />
          <MenuItem icon={MapPin} label="Delivery Address" onClick={() => setView('addresses')} />
          <div className="h-px bg-surface-border mx-4" />
          <MenuItem icon={Heart} label="Wishlist" value={wishlistCount > 0 ? `${wishlistCount} items` : ''} onClick={() => navigate('/wishlist')} />
          <div className="h-px bg-surface-border mx-4" />
          <MenuItem
            icon={Gift}
            label="My Promotions"
            badge={unreadPromos > 0 ? unreadPromos : undefined}
            onClick={() => navigate('/promotions')}
          />
        </div>

        <div className="bg-surface-card rounded-2xl border border-surface-border mx-4 overflow-hidden">
          <MenuItem icon={Bell} label="Notifications" onClick={() => navigate('/notifications')} />
          <div className="h-px bg-surface-border mx-4" />
          <MenuItem icon={Shield} label="Two-Factor Auth" onClick={() => setView('2fa')} />
          <div className="h-px bg-surface-border mx-4" />
          <MenuItem icon={HelpCircle} label="Help & Support" onClick={() => toast('Coming soon', { icon: '❓' })} />
        </div>

        <div className="bg-surface-card rounded-2xl border border-surface-border mx-4 overflow-hidden">
          <MenuItem icon={LogOut} label="Sign Out" onClick={handleLogout} danger />
        </div>

        <p className="text-center text-gray-600 text-xs font-display pb-2">
          Tony's Liquor Distributor
        </p>
      </div>
    </div>
  )
}
